/**
 * AssetProductTab
 *
 * Product search tab: Search products and select images
 * Compact version of /v2/products for asset selection
 *
 * @accessibility WCAG 2.1 AA compliant
 */

import { useState, useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Package, Search, Loader2, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getProducts, getProductImages, getProduct } from '@/lib/api/products';
import type { Product } from '@/types/products';
import { getPresignedUrl } from '@/lib/helpers/asset-urls';
import { ProductDetailPanel } from './ProductDetailPanel';

interface AssetProductTabProps {
  onImageSelect: (file: File | null) => void;
  selectedBrandId: string | null;
  onBrandChange: (brandId: string | null) => void;
  preselectedProductId?: string | null;
  /** Callback when a product is selected */
  onProductSelect?: (product: { id: string; name: string; sku?: string } | null) => void;
}

export function AssetProductTab({
  onImageSelect,
  selectedBrandId,
  onBrandChange,
  preselectedProductId,
  onProductSelect,
}: AssetProductTabProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [productThumbnails, setProductThumbnails] = useState<Record<string, string>>({});

  // Query for products
  const productsQuery = useQuery({
    queryKey: ['products-compact', { search }],
    queryFn: () => getProducts({ page: 1, page_size: 20, search: search || undefined }),
    staleTime: 30000,
  });

  const loadProductThumbnail = async (productId: string) => {
    if (productThumbnails[productId]) return;

    try {
      const response = await getProductImages(productId);
      if (response.success && response.images.length > 0) {
        const firstImage = response.images[0];
        const thumbnailUrl = firstImage.thumbnail_url || firstImage.s3_url;
        if (thumbnailUrl) {
          const presignedUrl = getPresignedUrl(thumbnailUrl);
          setProductThumbnails(prev => ({ ...prev, [productId]: presignedUrl }));
        }
      }
    } catch (error) {
      console.error('Error loading product thumbnail:', error);
    }
  };

  // Auto-load product if preselectedProductId is provided
  useEffect(() => {
    if (!preselectedProductId || selectedProduct) return;
    console.log('[AssetProductTab] Auto-loading product:', preselectedProductId);
    const loadPreselected = async () => {
      try {
        const response = await getProduct(preselectedProductId);
        if (response.success && response.product) {
          console.log('[AssetProductTab] Preselected product loaded:', response.product);
          setSelectedProduct(response.product);
          loadProductThumbnail(preselectedProductId);
          onProductSelect?.({
            id: response.product.id,
            name: response.product.name,
            sku: response.product.sku ?? undefined,
          });
        }
      } catch (error) {
        console.error('[AssetProductTab] Error loading preselected product:', error);
      }
    };
    loadPreselected();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- only on preselectedProductId change
  }, [preselectedProductId]);

  const handleSearch = useCallback(() => {
    setSearch(searchInput);
  }, [searchInput]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleProductClick = (product: Product) => {
    setSelectedProduct(product);
    // Load thumbnail for this product in background
    loadProductThumbnail(product.id);
    // Notify parent about product selection
    onProductSelect?.({ id: product.id, name: product.name, sku: product.sku ?? undefined });
  };

  const handleBack = () => {
    setSelectedProduct(null);
    onProductSelect?.(null);
  };

  const handleImageSelect = (file: File, _imageUrl: string) => {
    console.log('[AssetProductTab] Image selected from detail panel');
    onImageSelect(file);
  };

  const products = productsQuery.data?.products || [];

  // Show detail panel if product selected
  if (selectedProduct) {
    return (
      <div className="relative h-full">
        <ProductDetailPanel
          product={selectedProduct}
          onBack={handleBack}
          onImageSelect={handleImageSelect}
          selectedBrandId={selectedBrandId}
          onBrandChange={onBrandChange}
        />
      </div>
    );
  }

  // Show product list
  return (
    <div className="flex flex-col h-full p-2 space-y-4">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-1">
          {t('generation.selectImageFromProducts')}
        </h3>
        <p className="text-sm text-neutral-600 dark:text-neutral-400">
          {t('generation.selectImageFromProducts')}
        </p>
      </div>

      {/* Search */}
      <div className="space-y-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400 pointer-events-none" />
          <input
            type="text"
            placeholder={t('common.search')}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleKeyDown}
            className="w-full pl-10 pr-3 py-2 text-sm border border-neutral-300 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
        </div>
        <button
          onClick={handleSearch}
          className="w-full px-3 py-2 text-sm font-medium rounded-lg bg-orange-500 text-white hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500"
        >
          {t('common.search')}
        </button>
      </div>

      {/* Product List */}
      <div className="flex-1 overflow-y-auto space-y-2">
        {productsQuery.isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
          </div>
        ) : products.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-neutral-500">
            <Package className="h-12 w-12 mb-2" />
            <p className="text-sm text-center">
              {search ? t('common.noResults') : t('common.search')}
            </p>
          </div>
        ) : (
          products.map((product: Product) => {
            return (
              <button
                key={product.id}
                onClick={() => handleProductClick(product)}
                className="w-full border border-neutral-200 dark:border-neutral-700 rounded-lg overflow-hidden hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
              >
                <div className="flex items-center gap-3 p-3">
                  {/* Product Thumbnail */}
                  {productThumbnails[product.id] ? (
                    <div className="w-12 h-12 flex-shrink-0 rounded-lg overflow-hidden bg-neutral-100 dark:bg-neutral-800">
                      <img
                        src={productThumbnails[product.id]}
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="w-12 h-12 flex-shrink-0 rounded-lg bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center">
                      <Package className="h-5 w-5 text-neutral-400" />
                    </div>
                  )}

                  {/* Product Info */}
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate">
                      {product.name}
                    </p>
                    {product.sku && (
                      <p className="text-xs text-neutral-500 truncate">SKU: {product.sku}</p>
                    )}
                  </div>

                  {/* Chevron */}
                  <div className="flex-shrink-0">
                    <ChevronRight className="h-4 w-4 text-neutral-500" />
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
